<template>
  <div
    class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
    :style="isCollapsed ? 'min-height: auto' : 'min-height: 520px'">
    <!-- Header -->
    <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-700 flex items-center">
          <i class="fas fa-chart-line text-gray-500 mr-2 text-xs" />
          User Statistics
        </h3>
        <div class="flex items-center space-x-2">
          <span class="text-xs text-gray-400">Live</span>
          <span class="relative flex h-1.5 w-1.5">
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span class="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
          </span>
          <button
            @click="isCollapsed = !isCollapsed"
            class="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <i
              :class="isCollapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"
              class="text-xs" />
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="!isCollapsed && isLoading"
      class="p-6 flex items-center justify-center">
      <div role="status">
        <svg
          aria-hidden="true"
          class="w-8 h-8 text-gray-200 animate-spin fill-gray-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <!-- eslint-disable max-len -->
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor" />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill" />
          <!-- eslint-enable max-len -->
        </svg>
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Content -->
    <div
      v-else-if="!isCollapsed"
      class="p-4">
      <!-- Total Users Card -->
      <div class="mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-4 text-white shadow-md">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-blue-100 text-xs font-medium uppercase tracking-wider">
              Total Organizations
            </p>
            <p class="text-2xl font-bold mt-0.5">
              {{ animatedTotal }}
            </p>
          </div>
          <div class="bg-white/20 rounded-full p-2.5">
            <i class="fas fa-building text-sm" />
          </div>
        </div>
      </div>

      <!-- Plan Distribution -->
      <div class="mb-4">
        <h4 class="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wider">
          Plan Distribution
        </h4>
        <div class="grid grid-cols-2 gap-2">
          <!-- GPP Card -->
          <div class="bg-emerald-50 rounded-lg p-3 border border-emerald-200 hover:shadow-sm transition-shadow">
            <div class="flex items-center justify-between mb-1.5">
              <span class="text-xs font-medium text-emerald-700">GPP</span>
              <span class="text-xs text-emerald-600 font-medium">
                {{ getPercentage('gpp') }}%
              </span>
            </div>
            <p class="text-xl font-semibold text-gray-900">
              {{ userStats.gpp || 0 }}
            </p>
            <Sparkline
              v-if="sparklineData.gpp.length > 0"
              :data="sparklineData.gpp"
              :color="'#0E9F6E'"
              :height="20"
              :width="60"
              class="mt-1.5" />
          </div>

          <!-- SiteSense Card -->
          <div class="bg-violet-50 rounded-lg p-3 border border-violet-200 hover:shadow-sm transition-shadow">
            <div class="flex items-center justify-between mb-1.5">
              <span class="text-xs font-medium text-violet-700">SiteSense</span>
              <span class="text-xs text-violet-600 font-medium">
                {{ getPercentage('sitesense') }}%
              </span>
            </div>
            <p class="text-xl font-semibold text-gray-900">
              {{ userStats.sitesense || 0 }}
            </p>
            <Sparkline
              v-if="sparklineData.sitesense.length > 0"
              :data="sparklineData.sitesense"
              :color="'#7E3AF2'"
              :height="20"
              :width="60"
              class="mt-1.5" />
          </div>

          <!-- Bundle Card -->
          <div class="bg-amber-50 rounded-lg p-3 border border-amber-200 hover:shadow-sm transition-shadow">
            <div class="flex items-center justify-between mb-1.5">
              <span class="text-xs font-medium text-amber-700">Bundle</span>
              <span class="text-xs text-amber-600 font-medium">
                {{ getPercentage('bundle') }}%
              </span>
            </div>
            <p class="text-xl font-semibold text-gray-900">
              {{ userStats.bundle || 0 }}
            </p>
            <Sparkline
              v-if="sparklineData.bundle.length > 0"
              :data="sparklineData.bundle"
              :color="'#E3A008'"
              :height="20"
              :width="60"
              class="mt-1.5" />
          </div>

          <!-- Other Card -->
          <div class="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div class="flex items-center justify-between mb-1.5">
              <span class="text-xs font-medium text-gray-600">Other</span>
              <span class="text-xs text-gray-500">
                {{ getPercentage('other') }}%
              </span>
            </div>
            <p class="text-xl font-semibold text-gray-900">
              {{ userStats.other || 0 }}
            </p>
            <Sparkline
              v-if="sparklineData.other.length > 0"
              :data="sparklineData.other"
              :color="'#6b7280'"
              :height="20"
              :width="60"
              class="mt-1.5" />
          </div>
        </div>
      </div>

      <!-- Onboarding Progress -->
      <div class="bg-gray-50 rounded-lg p-3">
        <h4 class="text-xs font-semibold text-gray-600 mb-2.5 uppercase tracking-wider">
          Onboarding Pipeline
        </h4>

        <!-- Progress Bar -->
        <div class="mb-3">
          <div class="flex justify-between text-xs text-gray-500 mb-1">
            <span>Progress</span>
            <span>{{ getOnboardingPercentage() }}%</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
            <div
              class="bg-gradient-to-r from-red-500 via-amber-500 to-emerald-500 h-full rounded-full transition-all duration-1000 ease-out"
              :style="`width: ${getOnboardingPercentage()}%`" />
          </div>
        </div>

        <!-- Onboarding Stats -->
        <div class="grid grid-cols-3 gap-2">
          <div class="text-center">
            <div class="bg-white rounded-md p-2 border border-red-200">
              <p class="text-xs text-red-600 mb-0.5">
                Pending
              </p>
              <p class="text-base font-semibold text-gray-800">
                {{ formattedOnboardingStats['Pending Contact'] }}
              </p>
            </div>
          </div>

          <div class="text-center">
            <div class="bg-white rounded-md p-2 border border-amber-200">
              <p class="text-xs text-amber-600 mb-0.5">
                Contacted
              </p>
              <p class="text-base font-semibold text-gray-800">
                {{ formattedOnboardingStats['Contacted'] }}
              </p>
            </div>
          </div>

          <div class="text-center">
            <div class="bg-white rounded-md p-2 border border-emerald-200">
              <p class="text-xs text-emerald-600 mb-0.5">
                Onboarded
              </p>
              <p class="text-base font-semibold text-gray-800">
                {{ formattedOnboardingStats['Onboarded'] }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div v-if="!isCollapsed" class="px-4 py-2 bg-gray-50 border-t border-gray-200">
      <div class="flex items-center justify-between text-xs text-gray-400">
        <span>
          Auto-refresh: 30s
        </span>
        <span>
          {{ lastUpdated }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, onMounted, computed, onUnmounted
} from 'vue'
import axios from 'axios'
import Sparkline from '../components/sparkline.vue'

const userStats = ref({})
const isLoading = ref(true)
const isCollapsed = ref(false)
const animatedTotal = ref(0)
const lastUpdated = ref('')
const refreshInterval = ref(null)

// Mock sparkline data - in a real app, this would come from the API
const sparklineData = ref({
  gpp: [],
  sitesense: [],
  bundle: [],
  other: []
})

const animateValue = (targetRef, endValue, duration) => {
  const startValue = targetRef.value || 0
  const startTime = Date.now()
  const endTime = startTime + duration

  const step = () => {
    const now = Date.now()
    const remaining = Math.max(0, endTime - now)
    const progress = 1 - remaining / duration
    targetRef.value = Math.round(startValue + (endValue - startValue) * progress)

    if (remaining > 0) {
      requestAnimationFrame(step)
    }
  }

  requestAnimationFrame(step)
}

const generateSparklineData = () => {
  // Generate random trend data for sparklines
  const generateTrend = (baseValue) => {
    const data = []
    let current = baseValue * 0.8
    for (let i = 0; i < 7; i += 1) {
      current += (Math.random() - 0.3) * baseValue * 0.1
      data.push(Math.max(0, Math.round(current)))
    }
    return data
  }

  sparklineData.value = {
    gpp: generateTrend(userStats.value.gpp || 10),
    sitesense: generateTrend(userStats.value.sitesense || 10),
    bundle: generateTrend(userStats.value.bundle || 10),
    other: generateTrend(userStats.value.other || 10)
  }
}

const fetchUserStats = async() => {
  try {
    const response = await axios.get('/ajax/admin_dashboard/user_stats')
    userStats.value = response.data

    // Animate total count
    animateValue(animatedTotal, response.data.totalOrganizations || 0, 1000)

    // Generate mock sparkline data
    generateSparklineData()

    // Update last updated time
    lastUpdated.value = new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching user stats:', error)
  } finally {
    isLoading.value = false
  }
}

const formattedOnboardingStats = computed(() => {
  const onboardingStates = userStats.value.onboardingStates || {}
  return {
    'Pending Contact': onboardingStates.pendingContact || 0,
    Contacted: onboardingStates.contacted || 0,
    Onboarded: onboardingStates.onboarded || 0
  }
})

const getPercentage = (key) => {
  const total = userStats.value.totalOrganizations || 1
  const value = userStats.value[key] || 0
  return Math.round((value / total) * 100)
}

const getOnboardingPercentage = () => {
  const states = userStats.value.onboardingStates || {}
  // eslint-disable-next-line max-len
  const total = states.pendingContact + states.contacted + states.onboarded || 1
  const completed = states.onboarded || 0
  return Math.round((completed / total) * 100)
}

onMounted(() => {
  fetchUserStats()
  // Auto refresh every 30 seconds
  refreshInterval.value = setInterval(fetchUserStats, 30000)
})

onUnmounted(() => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
  }
})
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Add smooth transitions for hover effects */
.hover\:shadow-md {
  transition: box-shadow 0.2s ease;
}

/* Animate the live indicator */
@keyframes ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

.animate-ping {
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}
</style>
